import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import ProductApi from '../../apis/product';
import { resettingProductLoader } from '../slices/product';
import { RootState } from '../../redux/store';
import { CreateInvoiceParams, CreateProductParams } from '../../params/redux/product';

export const fetchInvoicesThunk = createAsyncThunk(
    'product/fetch-invoices',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProductApi.fetchInvoices(state.app.token, client_id);
            if (response?.success === 1) {
                return response.invoices;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProductLoader());
        }
    }
);

export const fetchProductsThunk = createAsyncThunk(
    'product/fetch',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProductApi.fetchProducts(state.app.token);
            if (response?.success === 1) {
                return response?.products;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProductLoader());
        }
    }
);

export const fetchAnalyticsThunk = createAsyncThunk(
    'product/analytics',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProductApi.fetchAnalytics(state.app.token, client_id);
            if (response?.success === 1) {
                return {
                    total_paid: response?.total_paid,
                    total_unpaid: response?.total_unpaid,
                    total_bill: response?.total_bill
                };
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProductLoader());
        }
    }
);

export const createInvoiceThunk = createAsyncThunk(
    'product/create-invoice',
    async (data: CreateInvoiceParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProductApi.createInvoice(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchAnalyticsThunk(data.client_id)).unwrap();
                await dispatch(fetchProductsThunk()).unwrap();
                await dispatch(fetchInvoicesThunk(data.client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProductLoader());
        }
    }
);

export const createProductThunk = createAsyncThunk(
    'product/create',
    async (data: CreateProductParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await ProductApi.createProduct(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingProductLoader());
        }
    }
);